/* eslint-disable max-len */
import React, { useState, Fragment, useEffect, useContext } from 'react';
import { Dialog, Transition } from '@headlessui/react';

import { Input, Card, Button, Spinner } from '..';

import google from '../../assets/images/google-icon.png';
import apple from '../../assets/images/apple-icon.svg';

import { loginWithGoogle, getRedirectResult, loginWithApple, login, checkUser } from '../../helpers/magic';
import { UserContext } from '../../context/userContext';
import { validateEmail } from '../../helpers/shared';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const SignIn = ({ isOpen, onClose, onSuccess }: Props) => {
  const [email, setEmail] = useState('');
  const [isEmailValid, setEmailValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);

  const { user, setUser } = useContext(UserContext);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (email.length <= 1) {
      setEmailValid(true);
    }
  };

  const handleClick = async () => {
    const isValid = Boolean(validateEmail(email));
    setEmailValid(Boolean(validateEmail(email)));

    if (isValid) {
      setIsLoading(true);
      try {
        await login(email);
        await checkUser(setUser);
        onSuccess();
        setIsLoading(false);
        setEmail('');
      } catch (e) {
        setIsLoading(false);
        // eslint-disable-next-line no-console
        console.log(e);
      }
    }
  };

  useEffect(() => {
    if (window.location.pathname.includes('redirect') && !user) {
      getRedirectResult(setUser);
    }
    if (window.location.pathname.includes('/redirect-apple') && !user) {
      setIsAppleLoading(true);
    } else if (window.location.pathname.includes('/redirect-google') && !user) {
      setIsGoogleLoading(true);
    } else if (user) {
      setIsAppleLoading(false);
      setIsGoogleLoading(false);
    }
  }, [user, setUser]);

  const handleGoogleClick = async () => {
    loginWithGoogle();
  };

  const handleAppleClick = async () => {
    loginWithApple();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as='div' className='fixed z-10 inset-0 overflow-y-auto' onClose={onClose}>
        <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </Transition.Child>

          <span className='hidden sm:inline-block sm:align-middle sm:h-screen' aria-hidden='true'>
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div
              className={`
              relative 
            inline-block 
            overflow-hidden 
            transform 
            transition-all s
            align-middle 
            sm:max-w-sm sm:w-full 
            `}
            >
              <Card size='small' theme='light' onClose={onClose}>
                <div>
                  <p className='text-xl leading-xl mb-5 mx-auto text-center font-bold'>Sign In</p>
                  <p className='text-sm leading-sm w-[200px] text-center mb-8 mx-auto'>
                    Enter your email to sign in or create an account
                  </p>
                  <div className='mb-3'>
                    <Input
                      placeholder='Your e-mail'
                      type='email'
                      required
                      value={email}
                      isInvalid={!isEmailValid}
                      error='Incorrect email address'
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className='mb-3.5'>
                    <Button theme='blue' size='medium' onClick={handleClick}>
                      {!isLoading ? <span>Continue</span> : <Spinner theme='blue' />}
                    </Button>
                  </div>

                  <div className='flex items-center mb-3.5'>
                    <div className='w-full h-px bg-main-grey-900 opacity-20' />
                    <p className='text-sm leading-sm mx-3.5'>or</p>
                    <div className='w-full h-px bg-main-grey-900 opacity-20' />
                  </div>

                  <div className='mb-2'>
                    <Button theme='light' size='medium' onClick={handleGoogleClick}>
                      {!isGoogleLoading ? (
                        <div className='flex items-center'>
                          <img src={google} alt='google icon' />
                          <p className='mt-1 ml-0.5'>Sign In with Google</p>
                        </div>
                      ) : (
                        <Spinner theme='light' />
                      )}
                    </Button>
                  </div>
                  <div className='mb-6'>
                    <Button theme='dark' size='medium' onClick={handleAppleClick}>
                      {!isAppleLoading ? (
                        <div className='flex items-center'>
                          <img src={apple} alt='apple icon' />
                          <p className='mt-1 ml-2'>Sign In with Apple</p>
                        </div>
                      ) : (
                        <Spinner theme='dark' />
                      )}
                    </Button>
                  </div>

                  <p className='text-sm leading-sm text-center w-[255px] mx-auto'>
                    By signing up you are agreeing to our{' '}
                    <a
                      className='text-black underline hover:no-underline'
                      href='https://endurable-weaver-d97.notion.site/Terms-Conditions-Tools-fbdcabcb12d942948055c022c3cf226b'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Terms of Use
                    </a>{' '}
                    &amp;{' '}
                    <a
                      className='text-black underline hover:no-underline'
                      href='https://endurable-weaver-d97.notion.site/Privacy-Policy-Tools-Products-87ad109325ae476a903172a44f41b393'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Privacy Policy
                    </a>
                  </p>
                </div>
              </Card>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export { SignIn };
